/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, {
   useCallback,
   useEffect,
   useMemo,
   useRef,
   useState,
} from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import styled from 'styled-components';

import { EmptyState } from '../../../styles/feedback/EmptyState';
import { Container } from '../../../styles/layout/Container';
import {
   PageHeader,
   PageHeaderText,
   PageTitle,
   PageSubtitle,
   PageHeaderActions,
} from '../../../styles/layout/PageHeader';
import { Section } from '../../../styles/layout/Section';
import { PrimaryButton, GhostButton } from '../../../styles/ui/Button';
import Modal from '../../../components/Modal/Modal';

import HFMap from './components/HFMap';
import HFRoutePanel from './components/HFRoutePanel';
import HFUnroutedDock from './components/HFUnroutedDock';
import HFTimeline from './components/HFTimeline';
import StopDrawer from './components/StopDrawer';
import SubclusterFormModal from './components/SubclusterFormModal';
import AssignTechnicianModal from './components/AssignTechnicianModal';
import FleetOptimizeModal from './components/FleetOptimizeModal';
import CancelledItemsModal from './components/CancelledItemsModal';
import OptimizeOverlay from './components/OptimizeOverlay';
import HFIcon from './icons';
import ROUTING_CAPS from './caps';

import adaptSnapshot, { adaptItemToStop } from './adapter';
import buildMapData from './mapData';
import { deriveTechStatus } from './derivations';
import { listServiceCatalog } from '../../../services/routing/routingApi';
import {
   getScheduleSnapshot,
   getClusterOrigins,
   groupSubclusterItems,
   moveItem,
   createSubcluster,
   updateSubcluster,
   deleteSubcluster,
   designateSubclusterTechnician,
   assignRoute,
   revertAssignedRoute,
   optimizeSchedule,
   getOptimizationStatus,
   mapFieldErrors,
   firstErrorCode,
   firstErrorMessage,
   DEFAULT_CLUSTER_ORIGIN,
} from '../../../services/routing/routingScheduleApi';

import {
   HF_TECHS,
   HF_T1_STOPS,
   HF_T2_STOPS,
   HF_T3_STOPS,
   HF_T4_STOPS,
   HF_UNROUTED,
   HF_ROUTES,
   HF_UNROUTED_PINS,
   HF_LEGEND,
} from './mockData';

import './styles.css';

// Board em largura total dentro do padrão DS (Container transparente sobre o
// fundo escuro do MainArea). Sem max-width — o mapa + dock + painel pedem a
// largura toda da área útil.
const BoardContainer = styled(Container)`
   max-width: none;
   padding-top: 12px;
`;

// "Voltar para listagem" — link sóbrio sobre o fundo escuro do MainArea, acima
// do cabeçalho (padrão de tela de detalhe). O clique passa por um modal de
// confirmação (board é workspace pesado; evita sair por engano).
const BackButton = styled.button`
   display: inline-flex;
   align-items: center;
   gap: 6px;
   background: transparent;
   border: none;
   color: ${({ theme }) =>
      theme?.colors?.textMutedOnAppBackground ?? 'rgba(255,255,255,0.72)'};
   font-size: 0.8rem;
   cursor: pointer;
   padding: 4px 0;
   margin-bottom: 8px;

   &:hover {
      color: ${({ theme }) => theme?.colors?.textOnAppBackground ?? '#fff'};
      text-decoration: underline;
   }
`;

function formatBRL(value) {
   return Number(value ?? 0).toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
   });
}

// ─── Datas (URL-driven, regra de base do projeto) ──────────────────────────
// A URL é a source of truth de ?date=YYYY-MM-DD&period=MANHA|TARDE. Sem
// params, default = hoje/MANHA (sem escrever na URL até o usuário mexer).
// ?mock=1 mantém o protótipo navegável sem backend (somente leitura local).

const YMD_RE = /^\d{4}-\d{2}-\d{2}$/;

// Piso de pedidos para liberar a otimização (handoff 0046 §4). Espelha o
// MIN_ITEMS_FOR_OPTIMIZATION do backend (RouteOptimizationService).
const MIN_ITEMS_FOR_OPTIMIZATION = 10;

function pad2(n) {
   return String(n).padStart(2, '0');
}

function dateToYmd(d) {
   return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function todayYmd() {
   return dateToYmd(new Date());
}

// Date às 12:00 local evita drift de timezone ao formatar/somar dias.
function ymdToDate(ymd) {
   return new Date(`${ymd}T12:00:00`);
}

// '31/05/2026 · sáb' — DD/MM/YYYY one-line (formato exigido pelo Caio).
function formatDateLabel(ymd) {
   const d = ymdToDate(ymd);
   const weekday = d
      .toLocaleDateString('pt-BR', { weekday: 'short' })
      .replace('.', '');
   return `${d.toLocaleDateString('pt-BR')} · ${weekday}`;
}

// ─── Mock (?mock=1) — protótipo no shape de subcluster ─────────────────────
// Cada "técnico" do mock vira um subcluster com o técnico já atribuído. CRUD /
// group-items / assign-technician ficam no-op no mock (persistência guardada
// por `if (!isMock)`); o objetivo é só manter a UI navegável sem backend.

const MOCK_STOPS_BY_SUBCLUSTER = {
   T1: HF_T1_STOPS,
   T2: HF_T2_STOPS,
   T3: HF_T3_STOPS,
   T4: HF_T4_STOPS,
   T5: [],
};

function mockTechToSubcluster(tech) {
   return {
      id: tech.id,
      subclusterId: tech.id,
      label: `Rota ${String(tech.name).split(' ')[0]}`,
      sequence: null,
      technician: {
         employeeId: tech.id,
         name: tech.name,
         init: tech.init,
         c: tech.c,
         department: null,
      },
      vehicle: tech.vehicle
         ? { id: null, identifier: tech.vehicle, type: null }
         : null,
      limits: { volumetry: null, travelTime: null, serviceTime: null },
      c: tech.c,
      status: tech.status,
      statusLbl: tech.statusLbl,
      summary: { total_items: 0, total_order_value: 0 },
      itemsCount: 0,
   };
}

const MOCK_SUBCLUSTERS = HF_TECHS.map(mockTechToSubcluster);
const MOCK_TECHNICIANS = HF_TECHS.map((tech) => ({
   employeeId: tech.id,
   name: tech.name,
   init: tech.init,
   c: tech.c,
   department: null,
   isAssigned: true,
}));

// Converte um pedido do dock mock pro shape de parada roteirizada. No fluxo
// real os itens do dock já são stops adaptados completos — basta clonar.
function unroutedToStop(item) {
   return {
      seq: 0,
      id: item.id,
      nm: item.nm,
      addr: item.addr,
      svc: item.svc,
      dur: 30,
      eta: '—',
      status: 'idle',
      sla: item.sla,
      vol: 'B',
   };
}

// Renumera a sequência das paradas após qualquer reordenação/inserção — é
// exatamente a sequência 1..N sem gaps que o group-items persiste.
function renumber(arr) {
   return arr.map((s, i) => ({ ...s, seq: i + 1 }));
}

// Referência estável de lista vazia — passada ao HFMap quando os pontos
// não-roteirizados estão ocultos, sem disparar o efeito de markers a cada render
// (um `[]` literal mudaria de identidade e re-rodaria o effect).
const EMPTY_LIST = [];

// Delta estimado só existe no mock — o real depende do otimizador (cap off).
function mockDelta(stop) {
   const dur = stop?.dur ?? 30;
   const km = (dur * 0.15).toFixed(1).replace('.', ',');
   return `+${dur}min · +${km}km`;
}

// Frase do modal de confirmação de drop — descreve a mudança pendente por tipo.
function dropSummaryMessage(summary) {
   if (!summary) return null;
   const item = (
      <>
         <strong>{summary.itemName || '—'}</strong>
         {summary.itemId ? ` (${summary.itemId})` : ''}
      </>
   );
   switch (summary.kind) {
      case 'reorder':
         return (
            <>
               Reordenar {item} na rota{' '}
               <strong>{summary.toLabel || '—'}</strong>: posição{' '}
               {summary.fromPos} → {summary.toPos}.
            </>
         );
      case 'move':
         return (
            <>
               Mover {item} da rota <strong>{summary.fromLabel || '—'}</strong>{' '}
               para <strong>{summary.toLabel || '—'}</strong> (posição{' '}
               {summary.toPos}).
            </>
         );
      case 'fromDock':
         return (
            <>
               Roteirizar {item} na rota{' '}
               <strong>{summary.toLabel || '—'}</strong> (posição {summary.toPos}
               ).
            </>
         );
      case 'toDock':
         return (
            <>
               Remover {item} da rota <strong>{summary.fromLabel || '—'}</strong>{' '}
               e devolver para <strong>Não roteirizados</strong>.
            </>
         );
      default:
         return null;
   }
}

export default function RouteManagement() {
   const permissions = useSelector(
      (state) => state.auth.user?.permissions || []
   );
   const canRead = permissions.includes('routing:schedule:read');
   const canManage = permissions.includes('routing:subcluster:manage');
   const canRecord = permissions.includes('routing:schedule:record_event');
   const canRemoveEvent = permissions.includes('routing:schedule:remove_event');
   // Edição de cargo_class (tag) e car_only (restrição VRP) na ficha — handoff
   // 0032. Mesma permissão para os dois campos.
   const canUpdateItem = permissions.includes('routing:schedule_item:update');
   // Excluir item (soft delete → CANCELADO): cancel (logística) OU
   // finance:operate. Backend exige PENDENTE; no board liberamos só para itens
   // NÃO roteirizados (dock) — desde o handoff 0059 a trava de item em rota é
   // UNIVERSAL (422 item_already_routed pra qualquer perfil), e isso fica
   // coberto pelo gate do dock.
   const canCancelItem =
      permissions.includes('routing:schedule_item:cancel') ||
      permissions.includes('routing:finance:operate');
   // Restaurar item cancelado (handoff 0059): EXCLUSIVO de quem tem cancel —
   // sem OR com finance:operate, assimetria intencional com o DELETE (decisão
   // de produto 2026-07-07). Sem a permissão, o card de excluídos segue
   // view-only; o backend responde 403 de qualquer forma.
   const canRestoreItem = permissions.includes('routing:schedule_item:cancel');
   // Disparo da otimização (handoff 0030 §1) — permissão própria, distinta do
   // manage de subcluster (quem só lê o board não vê o botão).
   const canOptimize = permissions.includes('routing:schedule:manage');

   const navigate = useNavigate();
   const location = useLocation();

   const [searchParams, setSearchParams] = useSearchParams();
   const isMock = searchParams.get('mock') === '1';

   const dateParam = searchParams.get('date');
   const date = dateParam && YMD_RE.test(dateParam) ? dateParam : todayYmd();
   const period = searchParams.get('period') === 'TARDE' ? 'TARDE' : 'MANHA';

   // Base operacional (Ponto 4) — terceira dimensão de navegação do board
   // (data/período/base). URL-driven via ?base=GOIANIA|ANAPOLIS; sem param,
   // default GOIANIA. Validada contra a lista real depois do fetch (abaixo).
   const baseParam = (searchParams.get('base') || '').toUpperCase();
   const clusterOrigin = baseParam || DEFAULT_CLUSTER_ORIGIN;

   // Visualização do board (handoff 0037 §B) — mapa (default) ou timeline
   // planejada. URL-driven via ?view=timeline; ausência = mapa.
   const view = searchParams.get('view') === 'timeline' ? 'timeline' : 'map';

   const updateParams = useCallback(
      (patch) => {
         const params = new URLSearchParams(searchParams);
         Object.entries(patch).forEach(([key, value]) => {
            if (value == null) params.delete(key);
            else params.set(key, value);
         });
         // replace: true — paginação de datas não polui o histórico; o Voltar
         // do browser sai da tela, não desfaz cliques de stepper.
         setSearchParams(params, { replace: true });
      },
      [searchParams, setSearchParams]
   );

   // ─── Estado do snapshot ──────────────────────────────────────────────
   const [subclusters, setSubclusters] = useState(
      isMock ? MOCK_SUBCLUSTERS : []
   );
   const [stopsBySubcluster, setStopsBySubcluster] = useState(
      isMock ? MOCK_STOPS_BY_SUBCLUSTER : {}
   );
   const [technicians, setTechnicians] = useState(
      isMock ? MOCK_TECHNICIANS : []
   );
   const [unrouted, setUnrouted] = useState(isMock ? HF_UNROUTED : []);
   // Itens excluídos (CANCELADO) — view-only (handoff 0048). Balde separado dos
   // vivos; alimenta o card "Itens excluídos (x)" e o modal de listagem.
   const [cancelledItems, setCancelledItems] = useState([]);
   const [cancelledPanelOpen, setCancelledPanelOpen] = useState(false);
   const [summary, setSummary] = useState(null);
   // Bases operacionais (fonte do seletor). Mock usa lista estática para manter
   // o protótipo navegável sem backend.
   const [clusterOrigins, setClusterOrigins] = useState([]);
   // Catálogo de serviços (handoff 0037 §A.1) — referência cacheável: tempo-
   // padrão por tipo de serviço, usado na sugestão de duração da classificação.
   const [serviceCatalog, setServiceCatalog] = useState([]);
   const [isLoading, setIsLoading] = useState(!isMock);
   const [loadError, setLoadError] = useState(null);
   const [isSaving, setIsSaving] = useState(false);

   // ETag de concorrência otimista — schedule.updated_at do último GET (ou do
   // último group-items 200). Ref em vez de state: só interessa às chamadas API.
   const scheduleRef = useRef(null);

   // Padrão: todas as rotas recolhidas na carga (sem auto-expandir nenhuma).
   const [openSubcluster, setOpenSubcluster] = useState(null);

   // Seleção atual: parada + subcluster (rota) que a contém.
   const [selected, setSelected] = useState(null);

   // Estado do drag — origem do item sendo arrastado.
   //   { source: 'dock' | 'route', id, item, subclusterId?, index? }
   const [drag, setDrag] = useState(null);
   // Alvo do drop atual: { subclusterId, index } ou { dock: true }
   const [dropTarget, setDropTarget] = useState(null);
   // Drop aguardando confirmação no modal (guard contra arrasto acidental).
   // Nada é aplicado ao board até o operador confirmar.
   //   { op, nextStops, nextUnrouted, openSubclusterId, summary } | null
   const [pendingDrop, setPendingDrop] = useState(null);

   // Modais de subcluster (CRUD + atribuição de técnico).
   //   subclusterForm = { mode: 'create' | 'edit', target } | null
   const [subclusterForm, setSubclusterForm] = useState(null);
   const [assignTarget, setAssignTarget] = useState(null);
   const [deleteTarget, setDeleteTarget] = useState(null);
   const [isDeleting, setIsDeleting] = useState(false);
   // Confirmação do "Voltar para listagem" (evita sair do board por engano).
   const [confirmBack, setConfirmBack] = useState(false);

   // ─── Otimização (handoff 0030) ───────────────────────────────────────
   // Base operacional com lat/lng (marcador do depósito no Google Maps).
   const [mapBase, setMapBase] = useState(null);
   // O botão Otimizar NÃO depende mais de last_optimized_at (handoff 0046 §4):
   // o portão é "cluster vazio" (zero rotas) + piso de pedidos prontos.
   const [fleetModalOpen, setFleetModalOpen] = useState(false);
   // Polling em curso + último state do semáforo (queued/processing) + % real.
   const [isOptimizing, setIsOptimizing] = useState(false);
   const [optimizeState, setOptimizeState] = useState(null);
   const [optimizeProgress, setOptimizeProgress] = useState(null);
   // Rotas cuja geometria ficou obsoleta por edição manual pós-otimização
   // (§5.3) — esmaecidas no mapa. Detecção de sessão; zera a cada snapshot.
   const [staleIds, setStaleIds] = useState(() => new Set());

   // Visibilidade de rotas no mapa (controle de view). Rotas no Set têm traçado
   // + ícones OCULTOS — permite isolar o trajeto de 1+ rotas. Estado de sessão.
   const [hiddenRouteIds, setHiddenRouteIds] = useState(() => new Set());

   // Visibilidade dos PONTOS não-roteirizados no mapa (toggle no cabeçalho do
   // dock, espelha o olho do painel de rotas). Estado de sessão.
   const [unroutedPointsHidden, setUnroutedPointsHidden] = useState(false);
   const handleToggleUnroutedVisibility = useCallback(
      () => setUnroutedPointsHidden((prev) => !prev),
      []
   );

   // Legenda de rotas colapsável (item de suporte). Preferência persistida —
   // expandida mostra os rótulos centralizados entre o dock e o painel; colapsada
   // vira um botão discreto ao lado do painel de rotas.
   const [legendCollapsed, setLegendCollapsed] = useState(
      () => localStorage.getItem('routing:legendCollapsed') === '1'
   );
   const toggleLegend = useCallback(() => {
      setLegendCollapsed((prev) => {
         const next = !prev;
         localStorage.setItem('routing:legendCollapsed', next ? '1' : '0');
         return next;
      });
   }, []);

   // Variação dark sóbria (Linear-style) — expor via toggle quando entrar
   // a fase de preferências de UI.
   const dark = false;

   // Drag liberado pra quem pode persistir (manage). Durante um group-items em
   // voo, segura o próximo drag — o ETag novo ainda não chegou.
   const canDrag = (isMock || canManage) && !isSaving;

   // ─── Bases operacionais (seletor) ────────────────────────────────────
   useEffect(() => {
      if (isMock) {
         setClusterOrigins([
            { origin_code: 'GOIANIA', origin_label: 'Goiânia' },
            { origin_code: 'ANAPOLIS', origin_label: 'Anápolis' },
         ]);
         return undefined;
      }
      let cancelled = false;
      getClusterOrigins()
         .then((list) => {
            if (!cancelled) setClusterOrigins(Array.isArray(list) ? list : []);
         })
         .catch(() => {
            // Silencioso: o seletor cai pro fallback da base atual; o snapshot
            // ainda funciona enviando a base da URL.
         });
      return () => {
         cancelled = true;
      };
   }, [isMock]);

   // ─── Catálogo de serviços (referência, carrega uma vez) ──────────────
   useEffect(() => {
      if (isMock) return undefined;
      let cancelled = false;
      listServiceCatalog()
         .then((list) => {
            if (!cancelled) setServiceCatalog(Array.isArray(list) ? list : []);
         })
         .catch(() => {
            // Silencioso: sem catálogo, a classificação cai no fallback de
            // rótulos e a sugestão de tempo fica 0 (operador personaliza à mão).
         });
      return () => {
         cancelled = true;
      };
   }, [isMock]);

   // Base inválida na URL (ex: deep-link antigo ou typo) → volta pro default.
   // Só corrige depois que a lista real chegou, pra não brigar com o fetch.
   useEffect(() => {
      if (isMock || clusterOrigins.length === 0) return;
      const known = clusterOrigins.some((o) => o.origin_code === clusterOrigin);
      if (!known) updateParams({ base: DEFAULT_CLUSTER_ORIGIN });
   }, [clusterOrigins, clusterOrigin, isMock, updateParams]);

   // ─── Snapshot (GET) ──────────────────────────────────────────────────
   const loadSnapshot = useCallback(async () => {
      if (isMock) return;
      setIsLoading(true);
      setLoadError(null);
      try {
         const snap = await getScheduleSnapshot(date, period, clusterOrigin);
         const adapted = adaptSnapshot(snap);
         scheduleRef.current = adapted.schedule;
         setSummary(adapted.summary);
         setSubclusters(adapted.subclusters);
         setStopsBySubcluster(adapted.stopsBySubcluster);
         setTechnicians(adapted.technicians);
         setUnrouted(adapted.unrouted);
         setCancelledItems(adapted.cancelled);
         setMapBase(adapted.clusterOrigin);
         // Geometria fresca do backend — zera as marcações de obsolescência.
         setStaleIds(new Set());
         setSelected(null);
         setDrag(null);
         setDropTarget(null);
         // Mantém aberta a rota que o usuário já tinha aberto (ex.: ao clicar
         // "Atualizar"); na carga inicial, tudo recolhido (sem auto-expandir).
         setOpenSubcluster((prev) =>
            adapted.subclusters.some((s) => s.id === prev) ? prev : null
         );
      } catch (err) {
         setLoadError(
            err?.response?.data?.message ??
               'Não foi possível carregar o agendamento. Verifique a conexão.'
         );
      } finally {
         setIsLoading(false);
      }
   }, [date, period, clusterOrigin, isMock]);

   useEffect(() => {
      loadSnapshot();
   }, [loadSnapshot]);

   // Restritor anti-spam do botão "Atualizar": após um clique manual, trava o
   // botão por 3s com contagem regressiva ("Atualizar (3)"…"(1)"). Só vale pro
   // clique do operador — as chamadas internas de loadSnapshot (pós-mutação)
   // não passam por aqui. refreshCooldown = segundos restantes (0 = liberado).
   const REFRESH_COOLDOWN_S = 3;
   const [refreshCooldown, setRefreshCooldown] = useState(0);
   const refreshCooldownRef = useRef(null);
   const handleManualRefresh = useCallback(() => {
      if (refreshCooldown > 0 || isLoading || isOptimizing) return;
      loadSnapshot();
      setRefreshCooldown(REFRESH_COOLDOWN_S);
      refreshCooldownRef.current = setInterval(() => {
         setRefreshCooldown((s) => {
            if (s <= 1) {
               clearInterval(refreshCooldownRef.current);
               return 0;
            }
            return s - 1;
         });
      }, 1000);
   }, [refreshCooldown, isLoading, isOptimizing, loadSnapshot]);
   useEffect(
      () => () => {
         if (refreshCooldownRef.current) {
            clearInterval(refreshCooldownRef.current);
         }
      },
      []
   );
   let refreshLabel = 'Atualizar';
   if (isLoading) refreshLabel = 'Atualizando…';
   else if (refreshCooldown > 0) refreshLabel = `Atualizar (${refreshCooldown})`;

   // ─── Derivações de exibição ──────────────────────────────────────────
   // Status agregado recalculado ao vivo — sobrevive a drag e a eventos sem
   // refetch. No mock preserva os statusLbl cenográficos (Atrasado etc.).
   const subclustersView = useMemo(() => {
      if (isMock) return subclusters;
      return subclusters.map((sub) => {
         const stops = stopsBySubcluster[sub.id] || [];
         const aggregate = deriveTechStatus(stops);
         return {
            ...sub,
            status: aggregate.status,
            statusLbl: aggregate.label,
            itemsCount: stops.length,
         };
      });
   }, [subclusters, stopsBySubcluster, isMock]);

   const mapData = useMemo(() => {
      if (isMock) {
         return {
            routes: HF_ROUTES.map((r) => ({ ...r, subclusterId: r.techId })),
            unroutedPins: HF_UNROUTED_PINS,
            legend: HF_LEGEND,
         };
      }
      return buildMapData(subclustersView, stopsBySubcluster, unrouted, {
         base: mapBase,
         staleSubclusterIds: staleIds,
      });
   }, [isMock, subclustersView, stopsBySubcluster, unrouted, mapBase, staleIds]);

   const routedCount = useMemo(
      () =>
         Object.values(stopsBySubcluster).reduce(
            (acc, arr) => acc + arr.length,
            0
         ),
      [stopsBySubcluster]
   );
   const totalItems = routedCount + unrouted.length;
   const routesActive = subclustersView.filter(
      (s) => (stopsBySubcluster[s.id] || []).length > 0
   ).length;
   // Contador do card "Itens excluídos" — usa o agregado do backend (handoff
   // 0048), com fallback no tamanho do balde.
   const cancelledCount = summary?.total_cancelled ?? cancelledItems.length;

   // Itens PENDENTES sem serviço associado (handoff 0037 §A.5) — a otimização
   // barra o disparo com 422 service_classification_required enquanto houver
   // qualquer um. Conta routed + dock para o pré-aviso do modal de frota.
   const unclassifiedPendingCount = useMemo(() => {
      if (isMock) return 0;
      const all = [
         ...Object.values(stopsBySubcluster).flat(),
         ...unrouted,
      ];
      return all.filter(
         (s) => s?.raw?.item_status === 'PENDENTE' && (s?.serviceTotal ?? 0) === 0
      ).length;
   }, [stopsBySubcluster, unrouted, isMock]);

   // Itens sem coordenada (needs_geocoding, handoff 0043) — a otimização barra
   // o disparo com 422 geocode_required enquanto houver qualquer um. Conta
   // routed + dock para o pré-aviso do modal de frota. O operador zera essa
   // lista fixando o ponto de cada item no mapa (ficha → Definir localização).
   const geocodeRequiredCount = useMemo(() => {
      if (isMock) return 0;
      const all = [...Object.values(stopsBySubcluster).flat(), ...unrouted];
      return all.filter((s) => s?.needsGeocoding).length;
   }, [stopsBySubcluster, unrouted, isMock]);

   // Pedidos PENDENTES "prontos" para otimizar (handoff 0046 §4) — PENDENTE com
   // serviço do catálogo E coordenada. Junto com "cluster vazio" (zero rotas),
   // forma o portão do botão Otimizar: piso de 10 prontos.
   const readyPendingCount = useMemo(() => {
      if (isMock) return 0;
      const all = [...Object.values(stopsBySubcluster).flat(), ...unrouted];
      return all.filter(
         (s) =>
            s?.raw?.item_status === 'PENDENTE' &&
            (s?.serviceTotal ?? 0) > 0 &&
            !s?.needsGeocoding
      ).length;
   }, [stopsBySubcluster, unrouted, isMock]);

   // ─── Visibilidade de rotas no mapa ───────────────────────────────────
   // Rotas no Set hiddenRouteIds não desenham traçado/ícones (filtro abaixo).
   // O painel mantém o card; o operador religa pelo olho.
   const visibleRoutes = useMemo(
      () => mapData.routes.filter((r) => !hiddenRouteIds.has(r.subclusterId)),
      [mapData.routes, hiddenRouteIds]
   );
   const allRoutesHidden =
      subclustersView.length > 0 &&
      subclustersView.every((s) => hiddenRouteIds.has(s.id));

   const handleToggleRouteVisibility = useCallback((id) => {
      setHiddenRouteIds((prev) => {
         const next = new Set(prev);
         if (next.has(id)) next.delete(id);
         else next.add(id);
         return next;
      });
   }, []);

   const handleToggleAllRoutesVisibility = useCallback(() => {
      setHiddenRouteIds((prev) => {
         const everyHidden =
            subclustersView.length > 0 &&
            subclustersView.every((s) => prev.has(s.id));
         // Tudo oculto → mostra tudo; caso contrário → oculta tudo.
         return everyHidden ? new Set() : new Set(subclustersView.map((s) => s.id));
      });
   }, [subclustersView]);

   // Topo do bloco: só "Roteirizados" com a contagem REAL de itens em rota
   // (routedCount), não o total de pedidos do cluster. "Rotas" foi reposicionado
   // para junto do "+ Nova rota" (ver routesCount no HFRoutePanel).
   const tabs = useMemo(
      () => [
         {
            key: 'roteirizados',
            label: 'Roteirizados',
            count: routedCount,
            active: true,
         },
      ],
      [routedCount]
   );

   // ─── Seleção / drawer ────────────────────────────────────────────────
   const handleStopClick = useCallback((stop, subcluster) => {
      setSelected({ stop, subcluster });
   }, []);

   // Item do dock (não-roteirizado) — abre a MESMA ficha (StopDrawer), sem
   // subcluster/técnico. Dá ao operador a via de ação quando o item é âncora
   // (M2): ver eventos ativos e remover o evento (LIFO) pra destravar, em vez
   // de ficar preso no quadro sem poder arrastar (Ponto 3).
   const handleUnroutedOpen = useCallback((item) => {
      setSelected({ stop: item, subcluster: null });
   }, []);

   // Clique no pino de item PENDENTE no mapa — abre a mesma ficha (#4). Busca o
   // stop por itemId na lista de não-roteirizados.
   const handleUnroutedPinClick = useCallback(
      (itemId) => {
         const stop = unrouted.find((u) => u.itemId === itemId);
         if (stop) setSelected({ stop, subcluster: null });
      },
      [unrouted]
   );

   // Clique no pino do mapa — busca a parada por subclusterId+seq e abre o
   // drawer. Também expande o card da rota pra dar contexto visual.
   const handlePinClick = useCallback(
      (subclusterId, seq) => {
         const subcluster = subclustersView.find((s) => s.id === subclusterId);
         const stop = (stopsBySubcluster[subclusterId] || []).find(
            (s) => s.seq === seq
         );
         if (!subcluster || !stop) return;
         setSelected({ stop, subcluster });
         setOpenSubcluster(subclusterId);
      },
      [subclustersView, stopsBySubcluster]
   );

   const closeDrawer = useCallback(() => setSelected(null), []);

   // Esc fecha o drawer — exceto quando um modal está aberto acima dele; o
   // Modal compartilhado trata o próprio Esc.
   useEffect(() => {
      if (!selected) return undefined;
      const onKeyDown = (e) => {
         if (e.key !== 'Escape') return;
         if (document.querySelector('[aria-modal="true"]')) return;
         closeDrawer();
      };
      window.addEventListener('keydown', onKeyDown);
      return () => window.removeEventListener('keydown', onKeyDown);
   }, [selected, closeDrawer]);

   // ─── Persistência do drag (handoff 0034) ────────────────────────────
   // Dois endpoints, por tipo de movimento:
   //   • reorder DENTRO da mesma rota → group-items (membership completa 1..N).
   //   • cross-rota / dock↔rota        → move (PATCH /items/:id/move), atômico.
   // Ambos recompõem mapa/ETA server-side; após o sucesso RE-BUSCAMOS o snapshot
   // (traz polyline/ETAs novos e o ETag atualizado). geometry_stale=true ⇒ ordem
   // salva mas o recompute falhou (Google indisponível) → marca a(s) rota(s)
   // como obsoleta(s) (mapa esmaece + badge), sem travar o trabalho (§6).

   // Marca rotas como obsoletas APÓS o refetch (loadSnapshot zera o staleIds).
   const markStale = useCallback((ids) => {
      const valid = ids.filter((id) => id != null).map(String);
      if (valid.length === 0) return;
      setStaleIds(new Set(valid));
   }, []);

   // Tratamento de erro comum: 409 stale_schedule · 422 por code (move/§4) ou
   // por campo (group-items) · demais. Sempre recarrega o snapshot pra desfazer
   // o estado otimista e refletir a verdade do backend.
   const handlePersistError = useCallback(
      async (err, fallbackMsg) => {
         const status = err?.response?.status;
         const apiMessage = err?.response?.data?.message;
         const code = firstErrorCode(err);
         if (status === 409 || code === 'stale_schedule') {
            toast.error(
               apiMessage ??
                  'O agendamento foi modificado por outro operador. Recarregando…'
            );
         } else if (status === 422) {
            const byCode = {
               vehicle_incompatible:
                  'Item “só carro” não pode entrar numa rota de moto.',
               // route_locked: rota atribuída recusa mudança de composição
               // (add/remove/cross-rota). O guard otimista já barra antes de
               // chegar aqui — esta é a rede de segurança contra corrida.
               route_locked:
                  'Rota atribuída — reverta a atribuição da rota para alterar a composição (adicionar, remover ou mover itens entre rotas).',
               // item_locked: item com atendimento iniciado/concluído (âncora M2).
               item_locked:
                  'Item com atendimento iniciado/concluído — não pode ser movido.',
               item_cancelled: 'Item cancelado não pode ser movido.',
               item_terminal: 'Item finalizado não pode ser movido.',
            };
            const fieldMessages = Object.values(mapFieldErrors(err));
            toast.error(
               byCode[code] ??
                  firstErrorMessage(err) ??
                  fieldMessages[0] ??
                  apiMessage ??
                  fallbackMsg
            );
         } else {
            toast.error(apiMessage ?? fallbackMsg);
         }
         await loadSnapshot();
      },
      [loadSnapshot]
   );

   // Reorder dentro de UMA rota (group-items). nextStops = estado otimista já
   // aplicado; affectedId = a rota reordenada.
   const persistReorder = useCallback(
      async (nextStops, affectedId) => {
         setIsSaving(true);
         try {
            const stops = nextStops[affectedId] || [];
            const assignments = stops.map((stop, index) => ({
               item_id: stop.itemId,
               sequence: index + 1,
            }));
            const result = await groupSubclusterItems(date, period, affectedId, {
               expected_schedule_updated_at:
                  scheduleRef.current?.updated_at ?? null,
               assignments,
            });
            await loadSnapshot();
            if (result.geometryStale) {
               markStale([affectedId]);
               toast.warn(
                  'Ordem salva, mas o mapa não pôde ser recalculado agora. Refaça a ordenação para tentar de novo.'
               );
            } else {
               toast.success('Rota atualizada.');
            }
         } catch (err) {
            await handlePersistError(err, 'Erro ao salvar a rota. Recarregando…');
         } finally {
            setIsSaving(false);
         }
      },
      [date, period, loadSnapshot, markStale, handlePersistError]
   );

   // Move um item para outra rota / para o não-roteirizado (dock) / entre rotas.
   // toSubclusterId numérico (ou null = dock); toSequence 1..N+1 (omitido p/ dock).
   const persistMove = useCallback(
      async (itemId, toSubclusterId, toSequence) => {
         setIsSaving(true);
         try {
            const payload = {
               expected_schedule_updated_at:
                  scheduleRef.current?.updated_at ?? null,
               to_subcluster_id: toSubclusterId,
            };
            if (toSubclusterId != null) payload.to_sequence = toSequence;
            const result = await moveItem(itemId, payload);
            await loadSnapshot();
            if (result.geometryStale) {
               markStale([result.sourceSubclusterId, result.destSubclusterId]);
               toast.warn(
                  'Item movido, mas o mapa não pôde ser recalculado agora. Refaça um ajuste para tentar de novo.'
               );
            } else {
               toast.success('Item movido.');
            }
         } catch (err) {
            await handlePersistError(err, 'Erro ao mover o item. Recarregando…');
         } finally {
            setIsSaving(false);
         }
      },
      [loadSnapshot, markStale, handlePersistError]
   );

   // ─── Drag handlers ───────────────────────────────────────────────────
   const handleDragStart = useCallback((payload) => {
      setDrag(payload);
      // Fecha o drawer pra evitar inconsistência se o stop selecionado for
      // movido durante o drag.
      setSelected(null);
   }, []);

   const handleDragEnd = useCallback(() => {
      setDrag(null);
      setDropTarget(null);
   }, []);

   const handleDragOver = useCallback((target) => {
      setDropTarget((prev) => {
         if (
            prev &&
            !prev.dock &&
            prev.subclusterId === target.subclusterId &&
            prev.index === target.index
         ) {
            return prev;
         }
         return target;
      });
   }, []);

   const handleDragLeave = useCallback(() => {
      // Não limpamos no leave — o próximo dragOver de outra linha sobrescreve.
      // Se limpássemos aqui, o estado piscaria entre rows adjacentes.
   }, []);

   const handleDockDragOver = useCallback(() => {
      setDropTarget((prev) => (prev?.dock ? prev : { dock: true }));
   }, []);

   // Esc cancela o drag em andamento. O cancel nativo do HTML5 já dispara
   // dragend (→ handleDragEnd limpa o estado); este listener é a garantia
   // explícita do atalho em qualquer browser/fluxo.
   useEffect(() => {
      if (!drag) return undefined;
      const onKeyDown = (e) => {
         if (e.key !== 'Escape') return;
         setDrag(null);
         setDropTarget(null);
      };
      window.addEventListener('keydown', onKeyDown);
      return () => window.removeEventListener('keydown', onKeyDown);
   }, [drag]);

   // Toast de route_locked (handoff 0041 D5) com CTA de reverter. Numa rota
   // ATRIBUIDA o reorder interno é liberado, mas alterar a COMPOSIÇÃO
   // (adicionar/remover/mover entre rotas) exige reverter a atribuição antes.
   // O handler de reverter é resolvido por ref porque é declarado mais abaixo —
   // evita reordenar todo o bloco de handlers só por causa da dependência.
   const revertRouteRef = useRef(null);
   const notifyRouteLocked = useCallback((assignedSub) => {
      toast.error(
         <div className="hf-toast-cta">
            <span>
               Rota atribuída — reverta a atribuição da rota para reorganizar a
               composição (adicionar, remover ou mover itens entre rotas).
            </span>
            {assignedSub && (
               <button
                  type="button"
                  className="hf-btn route-state-btn"
                  onClick={() => {
                     toast.dismiss();
                     revertRouteRef.current?.(assignedSub);
                  }}
               >
                  Reverter atribuição
               </button>
            )}
         </div>,
         { autoClose: 7000 }
      );
   }, []);

   const commitDrop = useCallback(() => {
      if (!drag || !dropTarget) return;

      // Item travado (M2 §2, code item_locked) é âncora fixa: tem atendimento
      // iniciado/concluído. Independe do estado da rota — não reordena, não muda
      // de rota, não desvincula. A linha já vem com draggable off, mas o guard
      // fecha qualquer brecha (ex: dock).
      if (drag.item?.locked) {
         toast.error(
            'Item com atendimento iniciado/concluído — não pode ser movido.'
         );
         setDrag(null);
         setDropTarget(null);
         return;
      }

      // Feasibility (handoff 0034 §5): item "só carro" não entra em rota de
      // moto. Pré-checagem no drop quando o item ESTÁ ENTRANDO numa rota nova
      // (dock→rota ou cross-rota) — vehicle_class vem no snapshot. Rota anônima
      // (vehicleClass null) permite. O backend ainda barra com 422
      // vehicle_incompatible (rede de segurança), mas evitamos a ida ao servidor.
      const enteringRoute =
         !dropTarget.dock &&
         (drag.source === 'dock' || drag.subclusterId !== dropTarget.subclusterId);
      if (enteringRoute && drag.item?.carOnly) {
         const destSub = subclusters.find((s) => s.id === dropTarget.subclusterId);
         if (destSub?.vehicleClass === 'moto') {
            toast.error('Item “só carro” não pode entrar numa rota de moto.');
            setDrag(null);
            setDropTarget(null);
            return;
         }
      }

      // Rota ATRIBUIDA (handoff 0041 D5, 2026-06-24): o backend RELAXOU a trava.
      // Agora aceita reorder DENTRO da própria rota (membership idêntica) e
      // recusa só a mudança de COMPOSIÇÃO: tirar item da rota (cross-rota/dock)
      // ou inserir item de fora → route_locked. Guard fino:
      //   • reorder na MESMA rota atribuída → passa;
      //   • origem OU destino atribuída e NÃO é reorder interno → bloqueia + CTA.
      const srcSub =
         drag.source === 'route'
            ? subclusters.find((s) => s.id === drag.subclusterId)
            : null;
      const destSub = !dropTarget.dock
         ? subclusters.find((s) => s.id === dropTarget.subclusterId)
         : null;
      const isReorderSameRoute =
         drag.source === 'route' &&
         !dropTarget.dock &&
         drag.subclusterId === dropTarget.subclusterId;
      let assignedSub = null;
      if (srcSub?.routeStatus === 'ATRIBUIDA') assignedSub = srcSub;
      else if (destSub?.routeStatus === 'ATRIBUIDA') assignedSub = destSub;
      if (assignedSub && !isReorderSameRoute) {
         notifyRouteLocked(assignedSub);
         setDrag(null);
         setDropTarget(null);
         return;
      }

      let nextStops = stopsBySubcluster;
      let nextUnrouted = unrouted;
      // op descreve a persistência a disparar (§4/§5):
      //   { kind: 'reorder', subclusterId }                         → group-items
      //   { kind: 'move', itemId, toSubclusterId, toSequence }      → move
      let op = null;
      // dropSummary alimenta o texto do modal de confirmação:
      //   { kind: 'reorder'|'move'|'fromDock'|'toDock', itemName, itemId,
      //     fromLabel?, toLabel?, fromPos?, toPos? }
      let dropSummary = null;

      if (dropTarget.dock) {
         // route → dock (volta pra não-roteirizado): move com destino null.
         if (drag.source !== 'route') {
            setDrag(null);
            setDropTarget(null);
            return;
         }
         const src = [...(stopsBySubcluster[drag.subclusterId] || [])];
         const [moved] = src.splice(drag.index, 1);
         if (!moved) {
            setDrag(null);
            setDropTarget(null);
            return;
         }
         nextStops = { ...stopsBySubcluster, [drag.subclusterId]: renumber(src) };
         nextUnrouted = [...unrouted, { ...moved, seq: null }];
         op = { kind: 'move', itemId: moved.itemId, toSubclusterId: null };
         dropSummary = {
            kind: 'toDock',
            itemName: moved.nm,
            itemId: moved.id,
            fromLabel: srcSub?.label,
         };
      } else if (drag.source === 'dock') {
         // dock → rota: no fluxo real o item do dock já é um stop adaptado
         // completo — só clona; ETA/seq são recalculados pelo backend.
         const newStop = isMock ? unroutedToStop(drag.item) : { ...drag.item };
         nextUnrouted = unrouted.filter((u) => u.id !== drag.id);
         const dest = [...(stopsBySubcluster[dropTarget.subclusterId] || [])];
         const idx = Math.min(dropTarget.index, dest.length);
         dest.splice(idx, 0, newStop);
         nextStops = {
            ...stopsBySubcluster,
            [dropTarget.subclusterId]: renumber(dest),
         };
         op = {
            kind: 'move',
            itemId: newStop.itemId,
            toSubclusterId: Number(dropTarget.subclusterId),
            toSequence: idx + 1,
         };
         dropSummary = {
            kind: 'fromDock',
            itemName: newStop.nm,
            itemId: newStop.id,
            toLabel: destSub?.label,
            toPos: idx + 1,
         };
      } else {
         // route → route (mesma rota ou cross-rota)
         // Soltar no mesmo lugar (índice normalizado idêntico ao de origem) não
         // é mudança de fato → sai sem persistir nem confirmar. Fecha o bug do
         // drop no-op que disparava group-items à toa.
         if (isReorderSameRoute) {
            let normIdx = dropTarget.index;
            if (drag.index < normIdx) normIdx -= 1;
            if (normIdx === drag.index) {
               setDrag(null);
               setDropTarget(null);
               return;
            }
         }
         const next = { ...stopsBySubcluster };
         const src = [...(next[drag.subclusterId] || [])];
         src.splice(drag.index, 1);
         next[drag.subclusterId] = renumber(src);

         const isSame = drag.subclusterId === dropTarget.subclusterId;
         const destArr = isSame
            ? [...next[dropTarget.subclusterId]]
            : [...(next[dropTarget.subclusterId] || [])];

         // Mesma rota e item original antes do alvo: o índice precisa ser
         // decrementado porque já foi removido.
         let idx = dropTarget.index;
         if (isSame && drag.index < dropTarget.index) idx -= 1;
         idx = Math.min(Math.max(idx, 0), destArr.length);

         destArr.splice(idx, 0, drag.item);
         next[dropTarget.subclusterId] = renumber(destArr);
         nextStops = next;

         op = isSame
            ? { kind: 'reorder', subclusterId: drag.subclusterId }
            : {
                 kind: 'move',
                 itemId: drag.item.itemId,
                 toSubclusterId: Number(dropTarget.subclusterId),
                 toSequence: idx + 1,
              };
         dropSummary = isSame
            ? {
                 kind: 'reorder',
                 itemName: drag.item.nm,
                 itemId: drag.item.id,
                 toLabel: destSub?.label,
                 fromPos: drag.index + 1,
                 toPos: idx + 1,
              }
            : {
                 kind: 'move',
                 itemName: drag.item.nm,
                 itemId: drag.item.id,
                 fromLabel: srcSub?.label,
                 toLabel: destSub?.label,
                 toPos: idx + 1,
              };
      }

      // Guard de âncora (M2 §2/§6 invalid_grouping/item_locked): mesmo sem
      // arrastar o item travado, mover OUTRO item pode empurrar a sequência de
      // um travado (ou esvaziar a posição antes dele). Comparamos a posição de
      // cada travado antes/depois — qualquer divergência aborta sem persistir.
      const lockedPositions = (stopsMap) => {
         const pos = {};
         Object.entries(stopsMap).forEach(([subclusterId, stops]) => {
            stops.forEach((stop, index) => {
               if (stop.locked) pos[stop.itemId] = `${subclusterId}:${index}`;
            });
         });
         return pos;
      };
      const before = lockedPositions(stopsBySubcluster);
      const after = lockedPositions(nextStops);
      const displacesAnchor = Object.keys(before).some(
         (itemId) => before[itemId] !== after[itemId]
      );
      if (displacesAnchor) {
         toast.error(
            'Há um item com atendimento iniciado/concluído nessa posição — ele não pode ser deslocado.'
         );
         setDrag(null);
         setDropTarget(null);
         return;
      }

      // Guard anti-arrasto acidental: o drop NÃO aplica nada aqui — vira um
      // pendingDrop e o board só muda quando o operador confirmar no modal
      // (confirmPendingDrop). Cancelar descarta sem efeito algum.
      setPendingDrop({
         op,
         nextStops,
         nextUnrouted,
         openSubclusterId: dropTarget.dock ? null : dropTarget.subclusterId,
         summary: dropSummary,
      });
      setDrag(null);
      setDropTarget(null);
   }, [
      drag,
      dropTarget,
      stopsBySubcluster,
      unrouted,
      isMock,
      subclusters,
      notifyRouteLocked,
   ]);

   // Confirmação do drop: aplica o update otimista e dispara a persistência.
   // Sucesso → re-busca o snapshot (mapa/ETA novos + ETag atualizado); falha →
   // loadSnapshot desfaz. A obsolescência de geometria é dirigida pelo
   // geometry_stale da resposta (markStale após o refetch), não marcada
   // preventivamente aqui.
   const confirmPendingDrop = useCallback(() => {
      if (!pendingDrop) return;
      const { op, nextStops, nextUnrouted, openSubclusterId } = pendingDrop;
      setStopsBySubcluster(nextStops);
      setUnrouted(nextUnrouted);
      // Expande o card da rota de destino p/ o operador conferir o encaixe
      // (handoff Card de Rota D1). Drop no dock não abre nada.
      if (openSubclusterId != null) setOpenSubcluster(openSubclusterId);
      setPendingDrop(null);
      if (!isMock && op) {
         if (op.kind === 'reorder') {
            persistReorder(nextStops, op.subclusterId);
         } else {
            persistMove(op.itemId, op.toSubclusterId, op.toSequence);
         }
      }
   }, [pendingDrop, isMock, persistReorder, persistMove]);

   const cancelPendingDrop = useCallback(() => setPendingDrop(null), []);

   // Pílula flutuante com a posição de destino (delta estimado só no mock).
   const deltaPill = useMemo(() => {
      if (!drag || !dropTarget || dropTarget.dock) return null;
      const stops = stopsBySubcluster[dropTarget.subclusterId] || [];
      // Só o reorder DENTRO da mesma rota mantém a contagem; dock→rota e
      // cross-subcluster ADICIONAM um item, então o total pós-drop é +1 (senão
      // o clamp trava o "soltar no fim" numa posição já existente em vez de
      // items+1).
      const isReorderSameRoute =
         drag.source === 'route' && drag.subclusterId === dropTarget.subclusterId;
      const totalAfter = stops.length + (isReorderSameRoute ? 0 : 1);
      const pos = Math.min(dropTarget.index + 1, totalAfter || 1);
      const text = isMock
         ? `Posição ${pos} · ${mockDelta(drag.item)} · Esc cancela`
         : `Posição ${pos} · Esc cancela`;
      return { subclusterId: dropTarget.subclusterId, text };
   }, [drag, dropTarget, stopsBySubcluster, isMock]);

   // ─── CRUD de subcluster + atribuição de técnico (perm manage) ────────
   const handleSubmitSubclusterForm = useCallback(
      async (body) => {
         let result;
         let affectedId;
         if (subclusterForm?.mode === 'edit') {
            affectedId = subclusterForm.target.subclusterId;
            result = await updateSubcluster(date, period, affectedId, body);
         } else {
            // Ponto 4: a nova rota nasce na base atualmente selecionada no board.
            result = await createSubcluster(date, period, {
               ...body,
               cluster_origin: clusterOrigin,
            });
            affectedId = result.subcluster?.id;
         }
         setSubclusterForm(null);
         await loadSnapshot();
         // T0 editado dispara recompute server-side (handoff 0055 §3).
         // geometry_stale=true = horário salvo mas o recompute falhou → ETAs/
         // geometria obsoletos (markStale após o refetch, igual reorder/move).
         if (result.geometryStale) {
            markStale([affectedId]);
            toast.warn(
               'Rota salva, mas os ETAs não puderam ser recalculados agora. Edite o horário de saída de novo para tentar novamente.'
            );
         } else {
            toast.success(result.message);
         }
      },
      [subclusterForm, date, period, clusterOrigin, loadSnapshot, markStale]
   );

   const handleConfirmDelete = useCallback(async () => {
      if (!deleteTarget) return;
      setIsDeleting(true);
      try {
         const result = await deleteSubcluster(
            date,
            period,
            deleteTarget.subclusterId
         );
         toast.success(result.message);
         setDeleteTarget(null);
         await loadSnapshot();
      } catch (err) {
         const apiMessage = err?.response?.data?.message;
         const code = err?.response?.data?.errors?.[0]?.field;
         if (err?.response?.status === 422 || code === 'subcluster_not_empty') {
            toast.error(
               apiMessage ??
                  'Esvazie a rota (mova os itens para outra rota ou para o dock) antes de removê-la.'
            );
         } else {
            toast.error(apiMessage ?? 'Erro ao remover a rota.');
         }
      } finally {
         setIsDeleting(false);
      }
   }, [deleteTarget, date, period, loadSnapshot]);

   // Designar técnico na ROTA (handoff 0042 §3) — planejamento; NÃO propaga aos
   // itens (isso é o "Atribuir rota", §4). O modal trata os erros 422 por code.
   const handleSubmitDesignate = useCallback(
      async (subclusterId, employeeId) => {
         const result = await designateSubclusterTechnician(
            date,
            period,
            subclusterId,
            { assigned_technician_employee_id: employeeId }
         );
         toast.success(result.message);
         setAssignTarget(null);
         await loadSnapshot();
      },
      [date, period, loadSnapshot]
   );

   // ─── Atribuir / reverter rota (handoff 0042 §4/§5) ───────────────────
   // Atribuir: propaga o técnico designado aos itens e TRAVA a rota (ATRIBUIDA).
   // Reverter: destrava (volta a DESIGNADA) para editar/mover itens. Ambos sem
   // body; re-busca o snapshot (itens + route_status mudam). Erros 422 por code.
   const handleAssignRoute = useCallback(
      async (sub) => {
         setIsSaving(true);
         try {
            const result = await assignRoute(date, period, sub.subclusterId);
            toast.success(result.message);
            await loadSnapshot();
         } catch (err) {
            const code = firstErrorCode(err);
            const apiMessage = err?.response?.data?.message;
            const byCode = {
               route_not_designated:
                  'Designe um técnico para a rota antes de atribuí-la.',
               route_already_assigned: 'Esta rota já está atribuída.',
            };
            toast.error(byCode[code] ?? apiMessage ?? 'Erro ao atribuir a rota.');
            await loadSnapshot();
         } finally {
            setIsSaving(false);
         }
      },
      [date, period, loadSnapshot]
   );

   const handleRevertRoute = useCallback(
      async (sub) => {
         setIsSaving(true);
         try {
            const result = await revertAssignedRoute(
               date,
               period,
               sub.subclusterId
            );
            toast.success(result.message);
            await loadSnapshot();
         } catch (err) {
            const code = firstErrorCode(err);
            const apiMessage = err?.response?.data?.message;
            const byCode = {
               route_not_assigned: 'Esta rota não está atribuída.',
            };
            toast.error(
               byCode[code] ?? apiMessage ?? 'Erro ao reverter a atribuição.'
            );
            await loadSnapshot();
         } finally {
            setIsSaving(false);
         }
      },
      [date, period, loadSnapshot]
   );

   // Mantém o ref do reverter sempre apontando pro handler atual — usado pelo
   // CTA do toast de route_locked (notifyRouteLocked), declarado antes deste.
   revertRouteRef.current = handleRevertRoute;

   // ─── Otimização: disparo + polling (handoff 0030 §1/§2) ──────────────
   // Disparo: o modal de frota chama isto. Sucesso = 202 → fecha o modal e
   // inicia o polling. Erro (cluster_not_empty/below_optimization_threshold/
   // geocode_required/service_classification_required/validation) é propagado
   // (throw) para o modal renderizar a UX por código — NÃO tratar aqui.
   const handleSubmitOptimize = useCallback(
      async (fleetParams, startTime, routeType) => {
         await optimizeSchedule(
            date,
            period,
            clusterOrigin,
            fleetParams,
            startTime,
            routeType
         );
         setFleetModalOpen(false);
         // Fecha o drawer e levanta o overlay (trava o board).
         setSelected(null);
         setOptimizeState('queued');
         setOptimizeProgress(0);
         setIsOptimizing(true);
         toast.info('Otimização enfileirada — acompanhando o progresso…');
      },
      [date, period, clusterOrigin]
   );

   // Polling do semáforo enquanto isOptimizing. O loop se reagenda via
   // setTimeout (~2.5s); para em completed/failed/none. completed → recarrega
   // o snapshot (traz rotas/ETAs/polyline). failed → toast com failed_reason
   // (nunca spinner infinito). Erro transitório de rede → segue tentando.
   useEffect(() => {
      if (isMock || !isOptimizing) return undefined;
      let cancelled = false;
      let timer = null;
      const poll = async () => {
         try {
            const status = await getOptimizationStatus(
               date,
               period,
               clusterOrigin
            );
            if (cancelled) return;
            setOptimizeState(status.state);
            if (status.progress != null) setOptimizeProgress(status.progress);
            if (status.state === 'completed') {
               setOptimizeProgress(100);
               setIsOptimizing(false);
               toast.success('Otimização concluída — rotas atualizadas.');
               await loadSnapshot();
               return;
            }
            if (status.state === 'failed') {
               setIsOptimizing(false);
               toast.error(
                  status.failedReason
                     ? `Otimização falhou: ${status.failedReason}`
                     : 'A otimização falhou.'
               );
               return;
            }
            if (status.state === 'none') {
               // Job sumiu (ex: navegou pra outra tripla) — encerra silencioso.
               setIsOptimizing(false);
               return;
            }
            // queued / processing → reagenda.
            timer = setTimeout(poll, 2500);
         } catch (err) {
            if (cancelled) return;
            // Transitório (5xx/rede) — não desiste; tenta de novo.
            timer = setTimeout(poll, 2500);
         }
      };
      timer = setTimeout(poll, 2000);
      return () => {
         cancelled = true;
         if (timer) clearTimeout(timer);
      };
   }, [isOptimizing, isMock, date, period, clusterOrigin, loadSnapshot]);

   // Resume: ao (re)carregar a tripla, se houver job em voo (reload no meio da
   // otimização), retoma o polling sem o operador precisar re-disparar.
   useEffect(() => {
      if (isMock || isOptimizing) return undefined;
      let cancelled = false;
      getOptimizationStatus(date, period, clusterOrigin)
         .then((status) => {
            if (cancelled) return;
            if (status.state === 'queued' || status.state === 'processing') {
               setOptimizeState(status.state);
               setOptimizeProgress(status.progress);
               setIsOptimizing(true);
            }
         })
         .catch(() => {
            // Silencioso — sem status, o operador ainda pode disparar manual.
         });
      return () => {
         cancelled = true;
      };
      // Só re-checa quando a tripla muda; isOptimizing fora das deps de
      // propósito (o efeito de polling cuida do ciclo de vida do job ativo).
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [date, period, clusterOrigin, isMock]);

   // ─── Update otimista de um item em todas as listas (board + drawer) ──
   // Aplica `applyToStop` aos stops de todos os subclusters, ao dock e ao stop
   // selecionado — compartilhado pelos handlers de registrar/remover evento.
   const applyStopUpdate = useCallback((applyToStop) => {
      setStopsBySubcluster((prev) => {
         const next = {};
         Object.entries(prev).forEach(([subclusterId, stops]) => {
            next[subclusterId] = stops.map(applyToStop);
         });
         return next;
      });
      setUnrouted((prev) => prev.map(applyToStop));
      setSelected((prev) =>
         prev ? { ...prev, stop: applyToStop(prev.stop) } : prev
      );
   }, []);

   // ─── Update otimista pós-evento (POST /events) ───────────────────────
   // item.item_status do response é o estado pós-espelho; o evento entra na
   // timeline local e o stop é re-adaptado — sem refetch.
   //
   // M2 §2/§4: qualquer evento ativo trava o item (locked=true) → vira âncora.
   // O sintético de um terminal "precoce" NÃO vem no response — a timeline
   // detalhada do drawer é re-buscada à parte (StopDrawer); o board só precisa
   // do status + locked, ambos derivados aqui.
   const handleEventRecorded = useCallback(
      ({ itemId, event, item }) => {
         const applyToStop = (stop) => {
            if (stop.itemId !== itemId || !stop.raw) return stop;
            const activeEvents = [...(stop.raw.active_events || [])];
            if (event) {
               activeEvents.push({
                  id: event.id,
                  event_kind: event.event_kind,
                  event_timestamp: event.event_timestamp,
                  is_synthetic: Boolean(event.is_synthetic),
               });
               activeEvents.sort((a, b) =>
                  String(a.event_timestamp).localeCompare(
                     String(b.event_timestamp)
                  )
               );
            }
            const nextRaw = {
               ...stop.raw,
               item_status: item?.item_status ?? stop.raw.item_status,
               active_events: activeEvents,
               locked: activeEvents.length > 0 ? true : stop.raw.locked,
            };
            return { ...adaptItemToStop(nextRaw), seq: stop.seq };
         };
         applyStopUpdate(applyToStop);
      },
      [applyStopUpdate]
   );

   // ─── Update otimista pós-remoção de evento (DELETE /events/:id) ──────
   // M2 §5: data.removed[] traz 1 ou 2 entradas (2 quando cascateou o
   // sintético). Tiramos esses eventos da timeline local, aplicamos o
   // item_status do retorno e recomputamos locked (item reabre/destrava
   // quando não sobra evento ativo).
   const handleEventRemoved = useCallback(
      ({ itemId, removed, item }) => {
         const removedIds = new Set((removed || []).map((ev) => ev.id));
         const applyToStop = (stop) => {
            if (stop.itemId !== itemId || !stop.raw) return stop;
            const activeEvents = (stop.raw.active_events || []).filter(
               (ev) => !removedIds.has(ev.id)
            );
            const nextRaw = {
               ...stop.raw,
               item_status: item?.item_status ?? stop.raw.item_status,
               active_events: activeEvents,
               locked: activeEvents.length > 0,
            };
            return { ...adaptItemToStop(nextRaw), seq: stop.seq };
         };
         applyStopUpdate(applyToStop);
      },
      [applyStopUpdate]
   );

   // ─── Update otimista pós-edição de cargo_class / car_only (handoff 0032) ─
   // O backend devolve o item completo serializado (data.scheduleItem). Mesclo
   // sobre o raw atual (preserva campos só-do-snapshot, ex: active_events) e
   // re-adapto o stop, preservando a seq da rota — igual aos handlers de evento.
   const handleItemUpdated = useCallback(
      ({ itemId, scheduleItem }) => {
         if (!scheduleItem) return;
         const applyToStop = (stop) => {
            if (stop.itemId !== itemId || !stop.raw) return stop;
            const nextRaw = { ...stop.raw, ...scheduleItem };
            return { ...adaptItemToStop(nextRaw), seq: stop.seq };
         };
         applyStopUpdate(applyToStop);
      },
      [applyStopUpdate]
   );

   // Item excluído (soft delete → CANCELADO). Some das listagens padrão, então
   // sai do snapshot: fecha a ficha e recarrega o board. Em corrida (422) o
   // dialog também chama isto p/ ressincronizar a UI com o estado real.
   const handleItemCancelled = useCallback(() => {
      setSelected(null);
      loadSnapshot();
   }, [loadSnapshot]);

   // "Voltar para listagem" (regra 1.6 navegacao.md): navigate(-1) restaura a
   // listagem filtrada exata (o card veio dela); deep-link direto (sem histórico)
   // cai no índice em cold-open.
   const handleBack = () => {
      setConfirmBack(false);
      if (location.key !== 'default') navigate(-1);
      else navigate('/routing/schedules');
   };

   if (!canRead) {
      return (
         <Container>
            <Section>
               <EmptyState
                  title="Sem permissão"
                  description="Você não tem acesso à gestão de rotas."
               />
            </Section>
         </Container>
      );
   }

   // ─── PageHeader (padrão DS) ──────────────────────────────────────────
   const periodLabel = period === 'MANHA' ? 'Manhã' : 'Tarde';
   const baseLabel =
      clusterOrigins.find((o) => o.origin_code === clusterOrigin)?.origin_label ||
      mapBase?.origin_label ||
      clusterOrigin;
   const headerTitle = `${formatDateLabel(date)} · ${periodLabel} · ${baseLabel}`;
   const headerStats = [
      `${routedCount}/${totalItems} roteirizados`,
      `${unrouted.length} não roteirizados`,
      `${routesActive}/${subclustersView.length} rotas ativas`,
   ];
   if (!isMock && summary?.total_order_value != null) {
      headerStats.push(formatBRL(summary.total_order_value));
   }
   const headerSubtitle = headerStats.join('  ·  ');

   return (
      <BoardContainer>
         <BackButton type="button" onClick={() => setConfirmBack(true)}>
            {HFIcon.chevL} Voltar para listagem
         </BackButton>

         <PageHeader>
            <PageHeaderText>
               <PageTitle>{headerTitle}</PageTitle>
               <PageSubtitle>{headerSubtitle}</PageSubtitle>
            </PageHeaderText>
            <PageHeaderActions>
               <div className="rt-view-toggle">
                  <button
                     type="button"
                     className={`opt ${view === 'map' ? 'active' : ''}`}
                     onClick={() => updateParams({ view: null })}
                  >
                     {HFIcon.map} Mapa
                  </button>
                  <button
                     type="button"
                     className={`opt ${view === 'timeline' ? 'active' : ''}`}
                     onClick={() => updateParams({ view: 'timeline' })}
                  >
                     {HFIcon.clock} Timeline
                  </button>
               </div>
               {!isMock && cancelledCount > 0 && (
                  <button
                     type="button"
                     className="rt-act-btn"
                     onClick={() => setCancelledPanelOpen(true)}
                     title="Ver os itens excluídos desta roteirização"
                  >
                     {HFIcon.trash} Itens excluídos ({cancelledCount})
                  </button>
               )}
               {!isMock && (
                  <button
                     type="button"
                     className="rt-act-btn"
                     onClick={handleManualRefresh}
                     disabled={isLoading || isOptimizing || refreshCooldown > 0}
                     title={
                        refreshCooldown > 0
                           ? 'Aguarde alguns segundos para atualizar de novo'
                           : undefined
                     }
                  >
                     {HFIcon.refresh} {refreshLabel}
                  </button>
               )}
               {ROUTING_CAPS.hasOptimizer && canOptimize && !isMock && (
                  <button
                     type="button"
                     className="rt-act-btn primary"
                     onClick={() => setFleetModalOpen(true)}
                     disabled={
                        isOptimizing ||
                        subclustersView.length > 0 ||
                        readyPendingCount < MIN_ITEMS_FOR_OPTIMIZATION
                     }
                     title={
                        // eslint-disable-next-line no-nested-ternary
                        subclustersView.length > 0
                           ? 'Há rotas nesta roteirização. Esvazie “Roteirizados” (remova as rotas) antes de otimizar.'
                           : readyPendingCount < MIN_ITEMS_FOR_OPTIMIZATION
                             ? `São necessários ao menos ${MIN_ITEMS_FOR_OPTIMIZATION} pedidos pendentes prontos (com serviço e localização) para otimizar — há ${readyPendingCount}.`
                             : undefined
                     }
                  >
                     {HFIcon.bolt} {isOptimizing ? 'Otimizando…' : 'Otimizar'}
                  </button>
               )}
            </PageHeaderActions>
         </PageHeader>

         <div className={`hf ${dark ? 'dark' : ''}`}>
            <div className="hf-stage">
            {loadError ? (
               <div
                  style={{
                     position: 'absolute',
                     inset: 0,
                     display: 'flex',
                     flexDirection: 'column',
                     alignItems: 'center',
                     justifyContent: 'center',
                     gap: 12,
                  }}
               >
                  <div style={{ fontSize: 14, color: '#8B8B82' }}>
                     {loadError}
                  </div>
                  <button
                     type="button"
                     className="hf-btn"
                     onClick={loadSnapshot}
                  >
                     {HFIcon.refresh} Tentar novamente
                  </button>
               </div>
            ) : (
               <>
                  {isLoading && (
                     <div
                        style={{
                           position: 'absolute',
                           top: 12,
                           left: '50%',
                           transform: 'translateX(-50%)',
                           zIndex: 30,
                           background: '#1F1F18',
                           color: '#fff',
                           borderRadius: 999,
                           padding: '6px 14px',
                           fontSize: 12,
                        }}
                     >
                        Carregando agendamento…
                     </div>
                  )}

                  {isOptimizing && !isLoading && (
                     <OptimizeOverlay
                        state={optimizeState}
                        progress={optimizeProgress}
                        tripla={{
                           dateLabel: formatDateLabel(date),
                           periodLabel: period === 'MANHA' ? 'Manhã' : 'Tarde',
                           baseLabel:
                              clusterOrigins.find(
                                 (o) => o.origin_code === clusterOrigin
                              )?.origin_label ||
                              mapBase?.origin_label ||
                              clusterOrigin,
                        }}
                     />
                  )}

                  {view === 'timeline' && (
                     <HFTimeline
                        subclusters={subclustersView}
                        stopsBySubcluster={stopsBySubcluster}
                        scheduleDate={date}
                        onStopClick={handleStopClick}
                     />
                  )}

                  {view !== 'timeline' && (
                  <>
                  <HFMap
                     dark={dark}
                     routes={visibleRoutes}
                     unroutedPins={
                        unroutedPointsHidden ? EMPTY_LIST : mapData.unroutedPins
                     }
                     unroutedGeo={
                        unroutedPointsHidden ? EMPTY_LIST : mapData.unroutedGeo
                     }
                     base={mapData.base}
                     forceSvg={isMock}
                     onPinClick={handlePinClick}
                     onUnroutedPinClick={handleUnroutedPinClick}
                     selectedItemId={selected?.stop?.itemId ?? null}
                     selected={
                        selected?.subcluster
                           ? {
                                subclusterId: selected.subcluster.id,
                                seq: selected.stop.seq,
                             }
                           : null
                     }
                  />

                  {/* Card e botão ficam SEMPRE montados — o cross-fade entre os
                      dois (via classe is-hidden) é o que permite animar abrir E
                      fechar. Render condicional desmontaria o elemento e mataria
                      a transição (como acontecia antes). */}
                  {mapData.legend.length > 0 && (
                     <>
                        <div
                           className={`hf-map-legend ${
                              legendCollapsed ? 'is-hidden' : ''
                           }`}
                           aria-hidden={legendCollapsed}
                        >
                           <div className="hf-map-legend-items">
                              {mapData.legend.map((l) => (
                                 <div key={l.name} className="it">
                                    <span
                                       className="sw"
                                       style={{ background: l.c }}
                                    />
                                    {l.name}
                                 </div>
                              ))}
                           </div>
                           <button
                              type="button"
                              className="hf-legend-toggle hide"
                              onClick={toggleLegend}
                              title="Ocultar a legenda de rotas"
                              tabIndex={legendCollapsed ? -1 : 0}
                           >
                              {HFIcon.chevR} Ocultar legendas
                           </button>
                        </div>
                        <button
                           type="button"
                           className={`hf-legend-toggle collapsed ${
                              legendCollapsed ? '' : 'is-hidden'
                           }`}
                           onClick={toggleLegend}
                           title="Mostrar a legenda de rotas"
                           aria-hidden={!legendCollapsed}
                           tabIndex={legendCollapsed ? 0 : -1}
                        >
                           {HFIcon.chevL} Expandir legendas
                        </button>
                     </>
                  )}

                  <HFRoutePanel
                     subclusters={subclustersView}
                     stopsBySubcluster={stopsBySubcluster}
                     openSubcluster={openSubcluster}
                     onOpenSubcluster={setOpenSubcluster}
                     tabs={tabs}
                     routesCount={subclustersView.length}
                     breachCount={summary?.time_window_breaches ?? 0}
                     onStopClick={handleStopClick}
                     selectedStopId={selected?.stop.id}
                     drag={drag}
                     dropTarget={dropTarget?.dock ? null : dropTarget}
                     staleIds={staleIds}
                     hiddenRouteIds={hiddenRouteIds}
                     onToggleVisibility={handleToggleRouteVisibility}
                     onToggleAllVisibility={handleToggleAllRoutesVisibility}
                     allRoutesHidden={allRoutesHidden}
                     canDrag={canDrag}
                     canManage={isMock || canManage}
                     emptyHint={
                        isLoading
                           ? 'Carregando…'
                           : 'Nenhuma rota neste agendamento. Crie a primeira rota.'
                     }
                     onDragStart={handleDragStart}
                     onDragEnd={handleDragEnd}
                     onDragOver={handleDragOver}
                     onDragLeave={handleDragLeave}
                     onDrop={commitDrop}
                     deltaPill={deltaPill}
                     onCreate={() => setSubclusterForm({ mode: 'create', target: null })}
                     onEdit={(sub) => setSubclusterForm({ mode: 'edit', target: sub })}
                     onDelete={(sub) => setDeleteTarget(sub)}
                     onAssign={(sub) => setAssignTarget(sub)}
                     onAssignRoute={handleAssignRoute}
                     onRevertRoute={handleRevertRoute}
                  />

                  <HFUnroutedDock
                     items={unrouted}
                     draggingId={drag?.source === 'dock' ? drag.id : null}
                     selectedItemId={
                        selected && !selected.subcluster
                           ? selected.stop?.itemId ?? null
                           : null
                     }
                     canDrag={canDrag}
                     isDropTarget={Boolean(dropTarget?.dock)}
                     onDragStart={handleDragStart}
                     onDragEnd={handleDragEnd}
                     onDockDragOver={
                        drag?.source === 'route' ? handleDockDragOver : null
                     }
                     onDockDrop={drag?.source === 'route' ? commitDrop : null}
                     onItemOpen={handleUnroutedOpen}
                     pointsHidden={unroutedPointsHidden}
                     onTogglePointsVisibility={handleToggleUnroutedVisibility}
                  />
                  </>
                  )}

                  {/* Ficha da parada — comum ao mapa e à timeline. */}
                  {selected && (
                     <StopDrawer
                        stop={selected.stop}
                        tech={selected.subcluster?.technician || null}
                        subcluster={selected.subcluster}
                        date={date}
                        period={period}
                        base={mapBase}
                        canRecord={canRecord}
                        canManage={canManage}
                        canRemoveEvent={canRemoveEvent}
                        canUpdateItem={canUpdateItem}
                        canCancelItem={canCancelItem}
                        catalog={serviceCatalog}
                        isMock={isMock}
                        onClose={closeDrawer}
                        onEventRecorded={handleEventRecorded}
                        onEventRemoved={handleEventRemoved}
                        onItemUpdated={handleItemUpdated}
                        onItemCancelled={handleItemCancelled}
                     />
                  )}
               </>
            )}
         </div>

         {subclusterForm && (
            <SubclusterFormModal
               mode={subclusterForm.mode}
               subcluster={subclusterForm.target}
               onClose={() => setSubclusterForm(null)}
               onSubmit={handleSubmitSubclusterForm}
            />
         )}

         {assignTarget && (
            <AssignTechnicianModal
               subcluster={assignTarget}
               technicians={technicians}
               onClose={() => setAssignTarget(null)}
               onSubmit={handleSubmitDesignate}
            />
         )}

         {fleetModalOpen && (
            <FleetOptimizeModal
               onClose={() => setFleetModalOpen(false)}
               onSubmit={handleSubmitOptimize}
               defaultStartTime={mapBase?.default_start_time ?? null}
               // Pré-seleção do retorno (handoff 0057): o route_type salvo da
               // roteirização vence; na abertura fria, a TARDE nasce 'one_way'
               // (técnico encerra na última parada) e a MANHA 'round_trip'.
               defaultRouteType={
                  scheduleRef.current?.route_type ??
                  (period === 'TARDE' ? 'one_way' : 'round_trip')
               }
               unclassifiedCount={unclassifiedPendingCount}
               geocodeRequiredCount={geocodeRequiredCount}
            />
         )}

         {cancelledPanelOpen && (
            <CancelledItemsModal
               items={cancelledItems}
               canRestore={canRestoreItem}
               // Restore recarrega o snapshot (item migra de cancelled[] pra
               // unrouted[] e os contadores se movem juntos). O modal fica
               // aberto — a lista encolhe com o refetch.
               onRestored={handleItemCancelled}
               onClose={() => setCancelledPanelOpen(false)}
            />
         )}

         <Modal
            isOpen={Boolean(deleteTarget)}
            onClose={() => (isDeleting ? null : setDeleteTarget(null))}
            title="Remover rota"
            maxWidth="420px"
            footer={
               <>
                  <button
                     type="button"
                     style={{
                        border: '1px solid rgba(0,0,0,0.15)',
                        borderRadius: 8,
                        padding: '8px 16px',
                        fontWeight: 600,
                        cursor: isDeleting ? 'not-allowed' : 'pointer',
                        background: 'transparent',
                     }}
                     onClick={() => setDeleteTarget(null)}
                     disabled={isDeleting}
                  >
                     Cancelar
                  </button>
                  <button
                     type="button"
                     style={{
                        border: 0,
                        borderRadius: 8,
                        padding: '8px 16px',
                        fontWeight: 600,
                        cursor: isDeleting ? 'not-allowed' : 'pointer',
                        background: '#b71c1c',
                        color: '#fff',
                        opacity: isDeleting ? 0.6 : 1,
                     }}
                     onClick={handleConfirmDelete}
                     disabled={isDeleting}
                  >
                     {isDeleting ? 'Removendo…' : 'Remover rota'}
                  </button>
               </>
            }
         >
            <div style={{ fontSize: 14, lineHeight: 1.5 }}>
               Remover a rota <strong>{deleteTarget?.label}</strong>? Só é
               possível remover rotas <strong>vazias</strong> — se ainda houver
               itens, mova-os para outra rota ou para o dock antes.
            </div>
         </Modal>

         <Modal
            isOpen={confirmBack}
            onClose={() => setConfirmBack(false)}
            title="Voltar para a listagem"
            maxWidth="420px"
            footer={
               <>
                  <button
                     type="button"
                     style={{
                        border: '1px solid rgba(0,0,0,0.15)',
                        borderRadius: 8,
                        padding: '8px 16px',
                        fontWeight: 600,
                        cursor: 'pointer',
                        background: 'transparent',
                     }}
                     onClick={() => setConfirmBack(false)}
                  >
                     Continuar aqui
                  </button>
                  <button
                     type="button"
                     style={{
                        border: 0,
                        borderRadius: 8,
                        padding: '8px 16px',
                        fontWeight: 600,
                        cursor: 'pointer',
                        background: '#1F1F18',
                        color: '#fff',
                     }}
                     onClick={handleBack}
                  >
                     Voltar para listagem
                  </button>
               </>
            }
         >
            <div style={{ fontSize: 14, lineHeight: 1.5 }}>
               Sair do quadro desta roteirização e voltar para a listagem? As
               alterações feitas no quadro já foram salvas.
            </div>
         </Modal>

         <Modal
            isOpen={Boolean(pendingDrop)}
            onClose={cancelPendingDrop}
            title="Confirmar alteração da rota"
            maxWidth="420px"
            footer={
               <>
                  <GhostButton type="button" onClick={cancelPendingDrop}>
                     Cancelar
                  </GhostButton>
                  <PrimaryButton type="button" onClick={confirmPendingDrop}>
                     Confirmar
                  </PrimaryButton>
               </>
            }
         >
            <div style={{ fontSize: 14, lineHeight: 1.5 }}>
               {dropSummaryMessage(pendingDrop?.summary)}
            </div>
         </Modal>
         </div>
      </BoardContainer>
   );
}
